/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `BusinessEvents` for the Caintra Connect frontend.
 * @module pages/BusinessEvents
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/BusinessEvents.module.css";

import { generateAssetUrl, truncateText } from "../utils/common.js";
import { AppContext } from "../context/AppContext";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";
import ResultsDropdown from "../components/ResultsDropdown";
import PageSelector from "../components/PageSelector";

import { CircularProgress, Backdrop } from "@mui/material";

import { use, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { getData, showEventCreatedSuccess } from "../logic/businessEvents";
import { isPastEvent } from "../utils/pastEventsAccess.js";
import {
  canManageBusinessEvents,
  canPerformEventListActions,
} from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";

import Caintra_Logo_03 from "../assets/Caintra_Logo_03.png";
import EventBanner from "../assets/EventBanner.png";

import { ReactComponent as CalendarIcon } from "../assets/icons/Calendar_Today.svg";
import { ReactComponent as SalesIcon } from "../assets/icons/Sales.svg";
import { ReactComponent as EditIcon } from "../assets/icons/Edit.svg";

import { ReactComponent as TitleIcon } from "../assets/icons/BusinessEvents/TitleIcon.svg";
import { ReactComponent as AddIcon } from "../assets/icons/BusinessEvents/AddIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../assets/icons/BusinessEvents/CheckCircleIcon.svg";
import { ReactComponent as MobileEditIcon } from "../assets/icons/BusinessEvents/MobileEditIcon.svg";
import AssetImage from "../components/AssetImage";
import { DEFAULT_RESULTS_LIMIT } from "../utils/resultsLimitOptions.js";

const BusinessEvents = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions, permissions } = use(AppContext);
  const { allowed } = useRequireAccess(canManageBusinessEvents);

  const [eventsData, setEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);

  // Device detection for responsive search bar
  const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1440;
  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;

  const getDevice = () => {
    if (isSmallMobile) {
      return "smallMobile";
    } else if (isMediumMobile) {
      return "mediumMobile";
    } else if (isLaptop) {
      return "laptop";
    } else if (isTablet) {
      return "tablet";
    } else {
      return "desktop";
    }
  };

  const getSearchBarWidth = (device) => {
    if (device === "laptop") {
      return "80.2%";
    } else if (device === "tablet") {
      return "73%";
    } else if (device === "mediumMobile") {
      return "90%";
    } else if (device === "smallMobile") {
      return "75%";
    } else {
      return "86%";
    }
  };

  useEffect(() => {
    if (!allowed) {
      return;
    }
    const initializeData = async () => {
      try {
        await fetchData();
      } catch (error) {
        logger.error("Error during initial data fetch:", {
          err: sanitizeError(error),
        });
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    initializeData();
  }, [allowed]);

  // Check if user just returned from creating an event
  useEffect(() => {
    if (location.state?.eventCreated) {
      handleShowEventCreatedSuccess();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const fetchData = async () => {
    try {
      const events = await getData(
        localStorage.getItem("userId"),
        changePermissions,
        navigate,
        setUser,
      );
      setEventsData(events);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  // Helper function to get the appropriate cost based on user type
  const getEventCost = (event) => {
    if (user?.tipoDeUsuario === "4") {
      return event.costoNoSocios || 0;
    } else if (["1", "2", "3", "5"].includes(user?.tipoDeUsuario)) {
      return event.costoSocios || 0;
    } else {
      return 0;
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleResultsLimitChange = (selectedOption) => {
    setResultsLimit(selectedOption.value);
    setCurrentPage(1);
  };

  const handlePageChange = (direction) => {
    if (
      direction === "next" &&
      currentPage < Math.ceil(eventsData.length / resultsLimit)
    ) {
      setCurrentPage((prevPage) => prevPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    } else {
      // Unsupported direction or already at bounds. No action required.
    }
  };

  const filteredEventsList = eventsData?.filter((event) =>
    event.nombre
      ? event.nombre.toLowerCase().includes(searchQuery.toLowerCase())
      : false,
  );

  const paginatedEventsList = filteredEventsList.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );

  const handleShowEventCreatedSuccess = () => {
    showEventCreatedSuccess(setShowSuccessMessage, setIsSlidingUp);
  };

  const canManage = canPerformEventListActions(
    user,
    permissions,
    canManageBusinessEvents,
  );

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Page title and search bar */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>{t("pages.businessEvents.header")}</h1>
          </div>
          <div className={styles.searchSection}>
            <SearchBar
              placeholder={
                isSmallMobile || isMediumMobile
                  ? t("pages.businessEvents.mobileSearchText")
                  : t("pages.businessEvents.searchText")
              }
              value={searchQuery}
              onChange={handleSearch}
              width={getSearchBarWidth(getDevice())}
            />
            {canManage && (
              <div
                className={styles.newRequirementButton}
                onClick={() =>
                  navigate("/businessEventForm", {
                    state: {
                      isEditMode: false,
                    },
                  })
                }
              >
                <AddIcon />
                {!isSmallMobile && !isMediumMobile ? (
                  <span>{t("pages.businessEvents.createEvent")}</span>
                ) : (
                  ""
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.eventsList}>
        {paginatedEventsList.length === 0 && (
          <div className={styles.noResults}>
            <p>{t("pages.businessEvents.noResults")}</p>
          </div>
        )}
        {paginatedEventsList.length > 0 &&
          paginatedEventsList.map((event, index) => (
            <div key={index} className={styles.eventCard}>
              <AssetImage
                className={styles.eventImage}
                src={
                  event.imagenBanner
                    ? generateAssetUrl(event.imagenBanner)
                    : EventBanner
                }
                alt={t("common.bannerAlt")}
              />
              <div className={styles.eventInfo}>
                <div className={styles.eventHeader}>
                  <div className={styles.eventHeaderTitle}>
                    <div className={styles.messageItemAvatar}>
                      <img
                        className={styles.avatar}
                        src={Caintra_Logo_03}
                        alt={t("common.avatarAlt")}
                      />
                    </div>
                    <span className={styles.eventName}>{event.nombre}</span>
                  </div>
                  {canManage && !isPastEvent(event) && (
                    <div className={styles.eventHeaderActions}>
                      <div
                        className={styles.buttonContainer}
                        onClick={() =>
                          navigate("/businessEventForm", {
                            state: {
                              isEditMode: true,
                              eventData: event,
                            },
                          })
                        }
                      >
                        {isSmallMobile || isMediumMobile ? (
                          <MobileEditIcon />
                        ) : (
                          <EditIcon className={styles.editButtonIcon} />
                        )}
                        <button className={styles.editButton}>
                          {t("pages.businessEvents.editEvent")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.eventDetails}>
                  <div className={styles.eventDates}>
                    <div className={styles.datesRow}>
                      <div className={styles.eventDate}>
                        <span className={styles.dayOfWeek}>
                          {dayjs(event.fechaInicioEvento).utc().format("dddd")}
                        </span>
                        <span className={styles.day}>
                          {dayjs(event.fechaInicioEvento).utc().format("DD")}
                        </span>
                        <span className={styles.monthAndYear}>
                          {dayjs(event.fechaInicioEvento).utc().format("MMMM")}
                          {", "}
                          {dayjs(event.fechaInicioEvento).utc().format("YYYY")}
                        </span>
                      </div>
                      <span>{t("pages.businessEvents.to")}</span>
                      <div className={styles.eventDate}>
                        <span className={styles.dayOfWeek}>
                          {dayjs(event.fechaFinEvento).utc().format("dddd")}
                        </span>
                        <span className={styles.day}>
                          {dayjs(event.fechaFinEvento).utc().format("DD")}
                        </span>
                        <span className={styles.monthAndYear}>
                          {dayjs(event.fechaFinEvento).utc().format("MMMM")}
                          {", "}
                          {dayjs(event.fechaFinEvento).utc().format("YYYY")}
                        </span>
                      </div>
                    </div>
                    {(user?.tipoDeUsuario === "11" ||
                      user?.tipoDeUsuario === "5" ||
                      user?.esColaborador ||
                      user?.compradorActivo) && (
                      <button
                        className={styles.viewAppointmentsButton}
                        onClick={() =>
                          navigate("/viewAppointments", { state: { event } })
                        }
                      >
                        {t("pages.businessEvents.viewAppointments")}
                      </button>
                    )}
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.eventDescriptionContainer}>
                    <span className={styles.eventDescription}>
                      {truncateText(event.descripcion, 50)}
                    </span>
                  </div>
                </div>
                <div className={styles.eventAdditionalDetails}>
                  {(() => {
                    const cost = getEventCost(event);
                    return cost > 0 ? (
                      <div className={styles.eventColumn}>
                        <div className={styles.eventColumnItem}>
                          <SalesIcon className={styles.salesIcon} />
                          <label className={styles.eventLabel}>
                            {t("pages.businessEvents.cost")}
                          </label>
                        </div>
                        <span className={styles.eventValue}>
                          {"$ " + cost + " MXN"}
                        </span>
                      </div>
                    ) : null;
                  })()}
                  {event.fechaLimiteRegistro && (
                    <div className={styles.eventColumn}>
                      <div className={styles.eventColumnItem}>
                        <CalendarIcon className={styles.salesIcon} />
                        <label className={styles.eventLabel}>
                          {t("pages.businessEvents.dueDateRegistration")}
                        </label>
                      </div>
                      <span className={styles.eventValue}>
                        {dayjs(event.fechaLimiteRegistro).utc().format("LLL")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className={styles.footer}>
        {/* Number of results dropdown */}
        <ResultsDropdown onResultsLimitChange={handleResultsLimitChange} />

        {/* Page selector */}
        <PageSelector
          currentPage={currentPage}
          totalPages={Math.ceil(filteredEventsList.length / resultsLimit)}
          onPageChange={handlePageChange}
        />
      </div>

      {showSuccessMessage && (
        <div
          className={`${styles.successMessage} ${
            isSlidingUp ? styles.slideUp : ""
          }`}
        >
          <CheckCircleIcon className={styles.successMessageIcon} />
          {t("pages.businessEvents.creationMessage")}
        </div>
      )}

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default BusinessEvents;
